/**
 * EvidenceTable — flat table of all unique evidence events referenced
 * by the current timeline segments.
 *
 * Displays event_id, source, completeness, and occurrence time (inferred
 * from the segment that references it). Since the movement endpoint doesn't
 * return per-event metadata, we derive what we can from the segments.
 */

import { useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import type { MovementSegment } from '../../../types/vehicleMovementTimeline';

interface EvidenceTableProps {
  segments: MovementSegment[];
}

interface EvidenceRow {
  event_id: string;
  source: string;
  completeness: string;
  segment_type: string;
  segment_start: string;
  segment_end: string;
}

function CopyCell({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* fallback silently */ }
  };
  return (
    <button
      onClick={handleCopy}
      className="p-0.5 rounded hover:bg-accent transition-colors"
      title="Copy event ID"
    >
      {copied ? (
        <Check className="w-3 h-3 text-success" />
      ) : (
        <Copy className="w-3 h-3 text-muted-foreground" />
      )}
    </button>
  );
}

export default function EvidenceTable({ segments }: EvidenceTableProps) {
  const rows = useMemo<EvidenceRow[]>(() => {
    const seen = new Set<string>();
    const result: EvidenceRow[] = [];
    for (const seg of segments) {
      for (const ref of seg.evidence_refs ?? []) {
        if (seen.has(ref)) continue;
        seen.add(ref);
        result.push({
          event_id: ref,
          source: seg.source?.replace(/_/g, ' ') ?? '—',
          completeness: seg.completeness ?? '—',
          segment_type: seg.type,
          segment_start: seg.start_ts,
          segment_end: seg.end_ts,
        });
      }
    }
    return result;
  }, [segments]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">Evidence Events</h3>
        <span className="text-xs text-muted-foreground">{rows.length} unique event{rows.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="overflow-x-auto max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-background sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Event ID</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Source</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Completeness</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Segment Type</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Time Range</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {rows.map((row) => (
              <tr key={row.event_id} className="hover:bg-secondary/30 transition-colors">
                <td className="px-3 py-1.5 font-mono text-foreground truncate max-w-50">
                  {row.event_id}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">{row.source}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{row.completeness}</td>
                <td className="px-3 py-1.5">
                  <span
                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      row.segment_type === 'MOVING'
                        ? 'bg-success/20 text-success'
                        : row.segment_type === 'STOPPED'
                          ? 'bg-warning/20 text-warning'
                          : 'bg-secondary text-muted-foreground'
                    }`}
                  >
                    {row.segment_type}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {new Date(row.segment_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}–
                  {new Date(row.segment_end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </td>
                <td className="px-2 py-1.5">
                  <CopyCell text={row.event_id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
